import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate } from 'react-router-dom'

import { getEnvironment } from '../../lib/environment'
import { Button } from '../../ui/Button/Button'
import { Input } from '../../ui/Input/Input'
import { Spinner } from '../../ui/Spinner/Spinner'
import { useAuth } from '../auth/AuthProvider'
import authStyles from '../auth/LoginPage.module.css'
import {
  getOnboardingApiClient,
  type OnboardingApiClient,
} from './onboarding-api'
import { onboardingStatusQueryKey } from './onboarding-query'
import { destinationPath, routeBootstrapResponse } from './onboarding-routing'

type SignupPageProps = {
  enabled?: boolean
  client?: OnboardingApiClient
}

export function SignupPage({
  enabled = getEnvironment().onboardingSignupEnabled,
  client = getOnboardingApiClient(),
}: SignupPageProps) {
  const auth = useAuth()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const submissionStarted = useRef(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmation, setConfirmation] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [recoveryRequired, setRecoveryRequired] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!enabled) return <Navigate to="/login" replace />
  if (auth.status === 'hydrating') {
    return <Spinner label="Restoring your session" />
  }
  if (
    auth.status === 'authenticated' &&
    !submissionStarted.current &&
    !recoveryRequired
  ) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (submitting || submissionStarted.current) return
    if (password !== confirmation) {
      setError('Passwords do not match.')
      return
    }

    submissionStarted.current = true
    setSubmitting(true)
    setError(null)
    setRecoveryRequired(false)
    let accountCreated = false
    try {
      const normalizedEmail = email.trim()
      const intent = await client.requestSignupIntent(normalizedEmail)
      const user = await auth.signUp(normalizedEmail, password)
      accountCreated = true
      const bootstrap = await client.bootstrap(user, intent)
      await queryClient.invalidateQueries({
        queryKey: onboardingStatusQueryKey(user.uid),
      })
      void navigate(destinationPath(routeBootstrapResponse(bootstrap)), {
        replace: true,
      })
    } catch {
      if (accountCreated) {
        setRecoveryRequired(true)
        setError(
          'Your account was created, but setup could not finish. Sign in again or retry setup later; your account was not deleted.'
        )
      } else {
        submissionStarted.current = false
        setError('Unable to create the account. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form
      className={authStyles.form}
      onSubmit={(event) => void handleSubmit(event)}
    >
      <h2>Create your clinic account</h2>
      <p>Start the secure owner setup for a new Flio clinic.</p>
      <div className={authStyles.fields}>
        <Input
          id="signup-email"
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          required
          disabled={submitting || recoveryRequired}
          onChange={(event) => setEmail(event.target.value)}
        />
        <Input
          id="signup-password"
          label="Password"
          type="password"
          autoComplete="new-password"
          value={password}
          required
          minLength={8}
          disabled={submitting || recoveryRequired}
          onChange={(event) => setPassword(event.target.value)}
        />
        <Input
          id="signup-confirmation"
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          value={confirmation}
          required
          minLength={8}
          disabled={submitting || recoveryRequired}
          onChange={(event) => setConfirmation(event.target.value)}
        />
      </div>
      {error ? (
        <p className={authStyles.error} role="alert">
          {error}
        </p>
      ) : null}
      {recoveryRequired ? (
        <Button
          className={authStyles.submit}
          type="button"
          onClick={() => void navigate('/onboarding', { replace: true })}
        >
          Continue setup
        </Button>
      ) : (
        <Button
          className={authStyles.submit}
          type="submit"
          disabled={submitting}
        >
          {submitting ? 'Creating account…' : 'Create account'}
        </Button>
      )}
      <p className={authStyles.secondaryAction}>
        Already have an account? <Link to="/login">Sign in</Link>
      </p>
    </form>
  )
}
